import { Link } from "react-router-dom";
import { Gem, Award, Sparkles, ArrowRight } from "lucide-react";
import { FlexImage } from "@/components/FlexImage";
import config from "@/config.json";

const iconMap = { Gem, Award, Sparkles };

const FeaturedProducts = () => {
  const featured = config.catalogue.filter((p) => p.featured).slice(0, 4);
  return (
    <section data-testid="featured-products-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#FDFCF8]">
      <div className="max-w-[1600px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#C5A059] mb-4 font-body">
              Featured Collection
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-[#1A1A1A]">
              Our Finest Pieces
            </h2>
          </div>
          <Link
            to="/catalogue"
            data-testid="view-full-catalogue-btn"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#1A1A1A] hover:text-[#C5A059] transition-colors duration-300 font-body group"
          >
            View Full Catalogue
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((product) => (
            <div
              key={product.id}
              data-testid={`featured-product-${product.id}`}
              className="group relative overflow-hidden bg-white border border-transparent hover:border-[#E5E5E5] transition-all duration-500"
            >
              <div className="aspect-[3/4] overflow-hidden">
                <FlexImage
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-in-out"
                />
              </div>
              <div className="p-5">
                <span className="text-xs uppercase tracking-[0.15em] text-[#C5A059] font-bold font-body">
                  {product.category}
                </span>
                <h3 className="font-heading text-lg font-medium mt-2 text-[#1A1A1A]">
                  {product.name}
                </h3>
                <p className="text-sm text-[#595959] mt-1 leading-relaxed font-body line-clamp-2">
                  {product.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function HomePage() {
  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative h-screen min-h-[600px] flex items-center">
        <div className="absolute inset-0">
          <FlexImage
            src={config.hero.image}
            alt="Hero jewellery"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
        </div>
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 w-full">
          <div className="max-w-2xl animate-fade-in">
            {/* <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#C5A059] mb-6 font-body">
              {config.business.name}
            </p> */}
            <h1 className="font-heading text-5xl md:text-7xl font-medium tracking-tight leading-none text-white mb-8">
              {config.hero.heading}
            </h1>
            <p className="text-base md:text-lg text-white/80 leading-relaxed mb-10 font-body max-w-lg">
              {config.hero.subheading}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/catalogue"
                data-testid="hero-explore-btn"
                className="inline-block rounded-none border border-white bg-white text-[#1A1A1A] px-8 py-3 hover:bg-transparent hover:text-white transition-all duration-300 uppercase tracking-[0.2em] text-xs font-bold"
              >
                Explore Collection
              </Link>
              <Link
                to="/contact"
                data-testid="hero-contact-btn"
                className="inline-block rounded-none border border-white/40 text-white px-8 py-3 hover:bg-white hover:text-[#1A1A1A] transition-all duration-300 uppercase tracking-[0.2em] text-xs font-bold"
              >
                Visit Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section data-testid="highlights-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F4F1EA]">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {config.highlights.map((item, i) => {
              const Icon = iconMap[item.icon] || Gem;
              return (
                <div
                  key={i}
                  data-testid={`highlight-${i}`}
                  className="p-12 bg-white/60 backdrop-blur-sm text-center space-y-5 hover:bg-white transition-all duration-500 animate-slide-up"
                  style={{ animationDelay: `${i * 150}ms` }}
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 border border-[#C5A059]/30 text-[#C5A059]">
                    <Icon size={24} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-2xl font-medium text-[#1A1A1A]">
                    {item.title}
                  </h3>
                  <p className="text-sm text-[#595959] leading-relaxed font-body max-w-xs mx-auto">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Owner Section */}
      <section data-testid="owner-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#FDFCF8]">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <div className="order-2 lg:order-1 animate-slide-up">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#C5A059] mb-4 font-body">
                Our Story
              </p>
              <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-[#1A1A1A] mb-8">
                The Art Behind Every Piece
              </h2>
              <p className="text-base md:text-lg text-[#595959] leading-relaxed font-body mb-8">
                {config.owner.bio}
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-[1px] bg-[#C5A059]" />
                <div>
                  <p className="font-heading text-lg font-medium text-[#1A1A1A]">
                    {config.owner.name}
                  </p>
                  <p className="text-xs uppercase tracking-[0.15em] text-[#595959] font-body">
                    {config.owner.title}
                  </p>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative aspect-[4/5] overflow-hidden">
                <FlexImage
                  src={config.owner.image}
                  alt={config.owner.name}
                  data-testid="owner-image"
                  className="w-full h-full object-cover rounded-sm"
                />
                <div className="absolute inset-0 border border-[#C5A059]/20 rounded-sm" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <FeaturedProducts />

      {/* CTA Banner */}
      <section data-testid="cta-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#1A1A1A] text-center">
        <div className="max-w-2xl mx-auto space-y-8 animate-fade-in">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#C5A059] font-body">
            Visit Our Store
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-white">
            Experience the Beauty in Person
          </h2>
          <p className="text-base text-white/60 leading-relaxed font-body">
            Our collection looks even more stunning up close. Visit us at our Zaveri Bazaar showroom
            to explore the complete range.
          </p>
          <Link
            to="/contact"
            data-testid="cta-contact-btn"
            className="inline-block rounded-none border border-[#C5A059] bg-[#C5A059] text-[#1A1A1A] px-10 py-3.5 hover:bg-transparent hover:text-[#C5A059] transition-all duration-300 uppercase tracking-[0.2em] text-xs font-bold"
          >
            Get Directions
          </Link>
        </div>
      </section>
    </main>
  );
}
